import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { ChevronDown, ChevronRight, FileText, Users, CreditCard, Shield, AlertTriangle, Scale, Mail, Book } from "lucide-react";

const TermsOfService = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (id) => {
    setExpandedSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const CollapsibleSection = ({ id, title, icon: Icon, children }) => {
    const isExpanded = expandedSections[id];
    return (
      <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden transition-shadow duration-300 hover:shadow-2xl">
        <button
          onClick={() => toggleSection(id)}
          className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-black/30 to-black/10 hover:from-black/40 hover:to-black/20 transition"
        >
          <div className="flex items-center space-x-3 text-white">
            <Icon className="w-5 h-5 text-gray-400" />
            <h2 className="text-lg font-light tracking-wide">{title}</h2>
          </div>
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {isExpanded && (
          <div className="px-6 py-5 text-gray-300 leading-relaxed space-y-4 border-t border-white/10">
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-black to-black text-white font-light border-b">
      <Helmet>
        <title>Terms of Service | Frost & Grey</title>
      </Helmet>

      {/* Hero */}
      <section className="relative py-20 text-center max-w-4xl mx-auto px-6">
        <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-5 py-2 mb-6 mx-auto">
        </div>
        <h1 className="text-5xl font-thin mb-4 tracking-tight">
          Terms of <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-white/60">Service</span>
        </h1>
        <p className="max-w-xl mx-auto text-gray-400 text-lg mb-6">
          The essential agreement for your journey with Frost & Grey. Please review these terms before using our services.
        </p>
        <p className="text-gray-500 text-sm tracking-widest uppercase">
          Last updated: June 16, 2025
        </p>
      </section>

      {/* Key Terms Summary */}
      <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6 mb-12 px-6">
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
          <FileText className="w-8 h-8 text-gray-400 mb-3" />
          <h3 className="text-lg font-semibold text-white mb-2">Our Commitment</h3>
            <p className="text-gray-300 text-sm">
            These terms set the standard for a positive and inspiring experience with Frost & Grey.
            </p>
        </div>
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
          <Shield className="w-8 h-8 text-gray-400 mb-3" />
          <h3 className="text-lg font-semibold text-white mb-2">Fair Usage</h3>
          <p className="text-gray-300 text-sm">Clear guidelines on acceptable use, intellectual property, and community standards.</p>
        </div>
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
          <CreditCard className="w-8 h-8 text-gray-400 mb-3" />
          <h3 className="text-lg font-semibold text-white mb-2">Transparent Billing</h3>
          <p className="text-gray-300 text-sm">Detailed payment terms, refund policies, and subscription management.</p>
        </div>
      </div>

      {/* Important Notice */}
      <div className="max-w-5xl mx-auto bg-red-50 border-l-4 border-red-400 p-6 rounded-lg mb-12 px-6">
        <div className="flex items-start">
          <AlertTriangle className="w-6 h-6 text-red-600 mt-1 mr-3" />
          <div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">Important Notice</h3>
            <p className="text-red-700">
              By accessing or using any Frost & Grey services, you agree to these Terms of Service. If you do not agree, discontinue use immediately. These terms include provisions on liability, dispute resolution, and intellectual property.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 pb-20 space-y-8">
        <CollapsibleSection id="acceptance" title="1. Acceptance & Scope" icon={FileText}>
          <p>
            By using Frost & Grey’s services, you acknowledge and accept these Terms. You must be at least 18 years old. These Terms apply to all our training, consulting, and digital services worldwide.
          </p>
        </CollapsibleSection>

        <CollapsibleSection id="services" title="2. Services & Programs" icon={Book}>
          <ul className="list-disc list-inside space-y-2">
            <li>AI training, courses, and certifications</li>
            <li>Custom AI implementation and consulting</li>
            <li>Workshops, webinars, and live sessions</li>
            <li>Digital platforms and community features</li>
          </ul>
        </CollapsibleSection>

        <CollapsibleSection id="accounts" title="3. Account Registration & Security" icon={Users}>
          <ul className="list-disc list-inside space-y-2">
            <li>Provide accurate and current information</li>
            <li>Keep your login credentials confidential</li>
            <li>Notify us immediately of unauthorized access</li>
          </ul>
        </CollapsibleSection>

        <CollapsibleSection id="payments" title="4. Payments & Billing" icon={CreditCard}>
          <ul className="list-disc list-inside space-y-2">
            <li>All fees are clearly stated and payable in advance unless otherwise specified</li>
            <li>Refunds are governed by our published refund policy</li>
            <li>Failure to pay may result in suspension of services</li>
          </ul>
        </CollapsibleSection>

        <CollapsibleSection id="intellectual" title="5. Intellectual Property" icon={Shield}>
          <ul className="list-disc list-inside space-y-2">
            <li>All content, branding, and materials are owned by Frost & Grey or its licensors</li>
            <li>You may not reproduce, distribute, or create derivative works without permission</li>
          </ul>
        </CollapsibleSection>

        <CollapsibleSection id="conduct" title="6. Acceptable Use & Conduct" icon={Users}>
          <ul className="list-disc list-inside space-y-2">
            <li>Respectful, professional conduct is required at all times</li>
            <li>No harassment, abuse, or unauthorized access</li>
            <li>Community guidelines apply to all forums and interactions</li>
          </ul>
        </CollapsibleSection>

        <CollapsibleSection id="liability" title="7. Limitation of Liability" icon={Scale}>
          <p>
            Frost & Grey is not liable for indirect, incidental, or consequential damages. Our total liability is limited to the amount paid for services.
          </p>
        </CollapsibleSection>

        <CollapsibleSection id="dispute" title="8. Dispute Resolution" icon={Scale}>
          <p>
            All disputes are subject to binding arbitration as detailed in Section 15 of the full terms. You waive the right to class actions and jury trials.
          </p>
        </CollapsibleSection>

        <CollapsibleSection id="changes" title="9. Changes to Terms" icon={FileText}>
          <p>
            We may update these Terms to reflect changes in law, technology, or business practices. Continued use of services constitutes acceptance of the new Terms.
          </p>
        </CollapsibleSection>

        <CollapsibleSection id="contact" title="10. Contact & Support" icon={Mail}>
          <p>
            For questions or concerns about these Terms, contact us at <a href="mailto:legal@frostgrey.com" className="underline text-indigo-400">legal@frostgrey.com</a>.
          </p>
        </CollapsibleSection>
      </main>
    </div>
  );
};

export default TermsOfService;
